import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { FetchSmsMessagesParams } from '../services/smsService';
import * as smsService from '../services/smsService';
import type { ThunkAPI } from '../store';
import { finaliseLogout } from '../store/thunks';
import { ApiSms } from '../types/Sms';

type FetchResult = { smsMessages: ApiSms[] } | undefined;

export interface SmsMessagesState {
  smsMessages: ApiSms[];
  lastFetchedDate?: string;
  isFetching: boolean;
  error?: SerializedError;
}

const initialState: SmsMessagesState = {
  smsMessages: [],
  lastFetchedDate: undefined,
  isFetching: false,
  error: undefined,
};

export const fetchSmSMessages = createAsyncThunk<
  FetchResult,
  { overrideParams?: FetchSmsMessagesParams },
  ThunkAPI
>('smsMessages/fetch', async ({ overrideParams = {} }, thunkAPI) => {
  const state = thunkAPI.getState();
  const customerId = state.client.activeCustomer?.id;

  if (!customerId) {
    return;
  }

  const res = await smsService.getSmsMessages(customerId, overrideParams);

  return { smsMessages: res };
});

const smsMessagesSlice = createSlice({
  name: 'smsMessages',
  initialState,
  reducers: {
    smsMessageReceived: (state, action: PayloadAction<ApiSms>) => {
      state.smsMessages.unshift(action.payload);
    },
    smsMessagesReset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSmSMessages.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchSmSMessages.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.error;
      })
      .addCase(
        fetchSmSMessages.fulfilled,
        (state, action: PayloadAction<FetchResult>) => {
          if (!action.payload) {
            return;
          }
          const smsMessages = [...state.smsMessages];
          const { smsMessages: newMessages } = action.payload;
          const smsMap = new Map();

          // No new messages!
          if (newMessages.length <= 0) {
            state.isFetching = false;
            state.error = undefined;
            return;
          }

          smsMessages.forEach((c, i) => smsMap.set(c.id, i));

          newMessages.forEach((n) => {
            const message = smsMap.get(n.id);
            if (!message && message !== 0) {
              smsMessages.push(n);
            }
          });

          smsMessages.sort((a, b) => {
            const keyA = new Date(a.created);
            const keyB = new Date(b.created);
            // Compare the 2 dates
            if (keyA < keyB) return 1;
            if (keyA > keyB) return -1;
            return 0;
          });

          const latestDate = smsMessages.reduce((acc, sms) => {
            if (!acc) {
              return sms.created;
            }
            return Date.parse(acc) < Date.parse(sms.created)
              ? sms.created
              : acc;
          }, '');

          state.smsMessages = smsMessages;
          if (
            latestDate &&
            (!state.lastFetchedDate ||
              Date.parse(latestDate) > Date.parse(state.lastFetchedDate))
          ) {
            state.lastFetchedDate = latestDate;
          }
          state.isFetching = false;
          state.error = undefined;
        },
      )
      .addCase(finaliseLogout.fulfilled, () => initialState);
  },
});

export const { smsMessagesReset, smsMessageReceived } =
  smsMessagesSlice.actions;

export default smsMessagesSlice.reducer;
